import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, Infinity, Star, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { getFeatureLabels } from "@/lib/platform-features";

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  max_orders_per_month: number | null;
  max_products: number | null;
  features: string[];
  is_featured: boolean;
  promo_price_monthly?: number | null;
  promo_price_yearly?: number | null;
  promo_label?: string | null;
  promo_ends_at?: string | null;
}

const PricingSection = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchPlans = async () => {
      try {
        // Use direct fetch to avoid auth state blocking on public data
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        
        const res = await fetch(
          `${supabaseUrl}/rest/v1/plans?select=*&is_active=eq.true&order=sort_order.asc`,
          {
            headers: {
              'apikey': supabaseKey,
              'Authorization': `Bearer ${supabaseKey}`,
              'Accept': 'application/json',
            },
          }
        );
        
        if (!mounted) return;
        
        if (!res.ok) {
          console.error("Error fetching plans:", res.status);
          setLoading(false);
          return;
        }

        const data = await res.json();
        const parsed = (data || []).map((p: any) => ({
          ...p,
          features: getFeatureLabels(p.features),
        }));
        setPlans(parsed);
      } catch (err) {
        console.error("Plans fetch exception:", err);
      }
      if (mounted) setLoading(false);
    };
    fetchPlans();
    return () => { mounted = false; };
  }, []);

  const hasPromo = (plan: Plan) => {
    if (!plan.promo_price_monthly && !plan.promo_price_yearly) return false;
    if (plan.promo_ends_at && new Date(plan.promo_ends_at) < new Date()) return false;
    return true;
  };

  const getPrice = (plan: Plan) => {
    const base = billing === "yearly" && plan.price_yearly != null ? plan.price_yearly : plan.price_monthly;
    if (hasPromo(plan)) {
      return billing === "yearly" && plan.promo_price_yearly
        ? plan.promo_price_yearly
        : plan.promo_price_monthly || base;
    }
    return base;
  };

  const getOriginalPrice = (plan: Plan) =>
    billing === "yearly" && plan.price_yearly != null ? plan.price_yearly : plan.price_monthly;

  const formatPrice = (v: number) =>
    v === 0 ? "Grátis" : `R$${v.toFixed(0)}`;

  return (
    <section id="pricing" className="py-24">
      <div className="container">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-primary text-sm font-semibold uppercase tracking-wider">Planos</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-4">
            Escolha o plano ideal
          </h2>
          <p className="text-muted-foreground text-lg mb-8">Comece grátis. Escale quando precisar.</p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 bg-muted rounded-full p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billing === "monthly" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBilling("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billing === "yearly" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Anual <span className="text-xs opacity-80">(-20%)</span>
            </button>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : plans.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">Nenhum plano disponível no momento.</p>
        ) : (
          <div className={`grid gap-8 max-w-6xl mx-auto ${
            plans.length <= 3 ? "md:grid-cols-3" : plans.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"
          }`}>
            {plans.map((plan, i) => {
              const price = getPrice(plan);
              const originalPrice = getOriginalPrice(plan);
              const isPromo = hasPromo(plan);
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-8 border transition-all duration-300 ${
                    plan.is_featured
                      ? "border-primary bg-gradient-card shadow-glow"
                      : "border-border bg-gradient-card shadow-card"
                  }`}
                  style={{ 
                    animation: `fadeInUp 0.5s ease-out ${i * 0.1}s both`
                  }}
                >
                  {isPromo && plan.promo_label && (
                    <div className="absolute -top-3 right-4 px-3 py-0.5 rounded-full bg-emerald-500 text-[10px] font-semibold text-white">
                      {plan.promo_label}
                    </div>
                  )}
                  {plan.is_featured && (
                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-primary text-xs font-semibold text-primary-foreground flex items-center gap-1`}>
                      <Star className="h-3 w-3" /> Mais Popular
                    </div>
                  )}
                  <h3 className="font-display text-xl font-bold">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                  <div className="mt-6 mb-1">
                    {isPromo && originalPrice !== price ? (
                      <>
                        <span className="text-lg line-through text-muted-foreground mr-2">{formatPrice(originalPrice)}</span>
                        <span className="text-4xl font-bold text-emerald-500">{formatPrice(price)}</span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold">{formatPrice(price)}</span>
                    )}
                    {price > 0 && <span className="text-muted-foreground">/{billing === "yearly" ? "ano" : "mês"}</span>}
                  </div>

                  {/* Limits */}
                  <div className="space-y-1 mb-6 mt-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Produtos</span>
                      <span className="font-medium">
                        {plan.max_products ? plan.max_products : (
                          <span className="flex items-center gap-0.5"><Infinity className="h-3 w-3" />Ilimitado</span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Pedidos/mês</span>
                      <span className="font-medium">
                        {plan.max_orders_per_month ? plan.max_orders_per_month.toLocaleString("pt-BR") : (
                          <span className="flex items-center gap-0.5"><Infinity className="h-3 w-3" />Ilimitado</span>
                        )}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate(`/cadastro?plan=${plan.id}`)}
                    className={`w-full h-12 font-semibold ${
                      plan.is_featured
                        ? "bg-gradient-primary text-primary-foreground hover:opacity-90"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                    }`}
                  >
                    {price === 0 ? "Começar Grátis" : "Começar Agora"}
                  </Button>
                  {plan.features.length > 0 && (
                    <ul className="mt-8 space-y-3">
                      {plan.features.map((f, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-sm">
                          <Check className="h-4 w-4 text-primary shrink-0" />
                          <span className="text-muted-foreground">{String(f)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingSection;